import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Newspaper, CheckCircle2, Send, Rss, TrendingUp, Activity } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";

export const Route = createFileRoute("/_authenticated/dashboard")({ component: Page });

function KPI({ icon: Icon, label, value, hint, accent }: any) {
  return (
    <Card className="relative overflow-hidden">
      <div className={`absolute inset-x-0 top-0 h-1 ${accent ?? "bg-primary"}`} />
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-3xl font-display font-semibold mt-1">{value}</div>
            {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
          </div>
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Page() {
  const { data } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const since = new Date(Date.now() - 7 * 86400000).toISOString();
      const [news, pending, sentToday, sources, byCat, byDay, latest] = await Promise.all([
        supabase.from("news_items").select("id", { count: "exact", head: true }).gte("created_at", new Date(Date.now() - 86400000).toISOString()),
        supabase.from("news_items").select("id", { count: "exact", head: true }).eq("approval_status", "pending"),
        supabase.from("telegram_send_logs").select("id", { count: "exact", head: true }).eq("send_status", "success").gte("sent_at", new Date(Date.now() - 86400000).toISOString()),
        supabase.from("news_sources").select("id", { count: "exact", head: true }).eq("status", true),
        supabase.from("news_items").select("asset_category_id, asset_categories(name_th, color)").gte("created_at", since),
        supabase.from("news_items").select("created_at").gte("created_at", since),
        supabase.from("news_items").select("id, original_title, created_at, status, approval_status, asset_categories(name_th, icon)").order("created_at", { ascending: false }).limit(10),
      ]);
      // aggregate by day
      const days: Record<string, number> = {};
      for (let i = 6; i >= 0; i--) {
        const d = new Date(Date.now() - i * 86400000); days[d.toISOString().slice(0, 10)] = 0;
      }
      (byDay.data ?? []).forEach((r: any) => {
        const k = r.created_at.slice(0, 10); if (k in days) days[k]++;
      });
      const dayChart = Object.entries(days).map(([d, n]) => ({ day: d.slice(5), count: n }));
      const cats: Record<string, { name: string; color: string; value: number }> = {};
      (byCat.data ?? []).forEach((r: any) => {
        const name = r.asset_categories?.name_th ?? "อื่น ๆ";
        const color = r.asset_categories?.color ?? "#888";
        cats[name] = cats[name] ?? { name, color, value: 0 };
        cats[name].value++;
      });
      return {
        newsCount: news.count ?? 0, pendingCount: pending.count ?? 0,
        sentTodayCount: sentToday.count ?? 0, activeSources: sources.count ?? 0,
        dayChart, catChart: Object.values(cats), latest: latest.data ?? [],
      };
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-primary/80 font-mono-meta">Investment Intelligence</div>
        <h1 className="text-3xl font-display font-semibold">Dashboard</h1>
        <p className="text-sm text-muted-foreground">ภาพรวมระบบดึงข่าว แปล และส่งเข้า Telegram แบบเรียลไทม์</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KPI icon={Newspaper} label="ข่าว 24 ชม." value={data?.newsCount ?? "—"} accent="bg-primary" />
        <KPI icon={CheckCircle2} label="รออนุมัติ" value={data?.pendingCount ?? "—"} accent="bg-amber-500" />
        <KPI icon={Send} label="ส่งสำเร็จวันนี้" value={data?.sentTodayCount ?? "—"} accent="bg-emerald-500" />
        <KPI icon={Rss} label="แหล่งข่าวทำงาน" value={data?.activeSources ?? "—"} accent="bg-sky-500" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary"/>ข่าวรายวัน 7 วันล่าสุด</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.dayChart ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(1 0 0 / 0.06)" />
                <XAxis dataKey="day" stroke="oklch(0.7 0.02 90)" fontSize={12}/>
                <YAxis stroke="oklch(0.7 0.02 90)" fontSize={12}/>
                <Tooltip contentStyle={{ background: "oklch(0.22 0.025 260)", border: "1px solid oklch(1 0 0 / 0.1)" }} />
                <Bar dataKey="count" fill="oklch(0.82 0.16 85)" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Activity className="h-4 w-4 text-primary"/>ตามประเภทสินทรัพย์</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data?.catChart ?? []} dataKey="value" nameKey="name" outerRadius={90}>
                  {(data?.catChart ?? []).map((c, i) => <Cell key={i} fill={c.color}/>)}
                </Pie>
                <Tooltip contentStyle={{ background: "oklch(0.22 0.025 260)", border: "1px solid oklch(1 0 0 / 0.1)" }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>ข่าวล่าสุด</CardTitle><CardDescription>10 รายการล่าสุดในระบบ</CardDescription></CardHeader>
        <CardContent>
          <div className="divide-y divide-border">
            {(data?.latest ?? []).map((n: any) => (
              <div key={n.id} className="py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{(n.asset_categories?.icon ?? "📰")} {n.original_title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleString("th-TH")} · {n.asset_categories?.name_th ?? "—"}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Badge variant="outline" className="text-xs">{n.status}</Badge>
                  <Badge className="text-xs">{n.approval_status}</Badge>
                </div>
              </div>
            ))}
            {(data?.latest ?? []).length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">ยังไม่มีข่าวในระบบ — ไปที่ News Sources แล้วกด Fetch Now</div>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}